import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["tabindex"]
const _hoisted_2 = {
  id: "transcriptViewer",
  ref: "transcriptViewer",
  class: "relative bg-stone flex flex-col gap-5 border-solid px-5 pt-5 sm:px-50px sm:pt-50px border-5px rounded-xl w-[90%] max-w-[800px] min-w-[300px] h-[90%] border-sprout text-slateGray pointer-events-auto"
}
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = { class: "text-bodyMd grow" }
const _hoisted_5 = ["tabindex"]
const _hoisted_6 = { class: "font-serif text-headingLg" }
const _hoisted_7 = ["innerHTML"]

import { reactive, ref } from 'vue'
import type { Ref } from 'vue'
import { useEventBus } from '@vueuse/core'
import { gsap } from 'gsap'
import { ButtonSizing, TranscriptPlaybackData } from '../general/types'
import useLanguage from '@/composables/useLanguage'
import BasicButton from './BasicButton.vue'
import { ButtonVariant } from './types'
import CloseIcon from '@/assets/svg/CloseIcon.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'TranscriptViewer',
  setup(__props) {

const transcriptOpenedBus = useEventBus('transcript-opened')
const transcriptClosedBus = useEventBus('transcript-closed')
const transcriptWrapper: Ref<HTMLElement | null> = ref(null)
const interfaceText = useLanguage('interface')
const transcriptText = useLanguage('transcripts')
const state = reactive({
  isOpen: false,
  transform: 'translate(0, 0) scale(0)',
  title: 'Transcript',
  text: '',
  isDisplayed: false,
})
let lastActiveElement
let firstButton
let lastButton
const keyCodes = {
  ESC: 27,
}

const globalEscape = (e) => {
  var keyCode = e.keyCode || e.which

  switch (keyCode) {
    case keyCodes.ESC:
      e.preventDefault()
      closeViewer()
      break

    default:
      break
  }
}

transcriptOpenedBus.on((info: unknown) => {
  const infoProxy = info as TranscriptPlaybackData
  if (transcriptWrapper.value) {
    const translateX = -(window.innerWidth * 0.5 - infoProxy.x)
    const translateY = -(window.innerHeight * 0.5 - infoProxy.y)
    state.transform = `translate(${translateX}px, ${translateY}px) scale(0)`
    state.title = transcriptText.value[infoProxy.transcript].title
    state.text = transcriptText.value[infoProxy.transcript].text
    gsap.fromTo(
      transcriptWrapper.value,
      { transform: state.transform, opacity: 0 },
      {
        transform: 'translate(0, 0) scale(1)',
        opacity: 1,
        duration: 0.75,
        ease: 'power4.inOut',
      }
    )
  }
  state.isDisplayed = true
  state.isOpen = true
  document.addEventListener('keydown', globalEscape, false)
  firstButton = document.getElementById('transcript-close-btn')
  lastButton = document.getElementById('transcript-close-bg-btn')
  trapFocus()
  console.log('focus', firstButton)
  setTimeout(() => {
    firstButton.focus()
  }, 500)
})

const closeViewer = () => {
  state.isOpen = false
  if (transcriptWrapper.value) {
    gsap.to(transcriptWrapper.value, {
      transform: state.transform,
      opacity: 0,
      duration: 0.75,
      ease: 'power4.inOut',
      onComplete: () => {
        state.isDisplayed = false
      },
    })
  } else {
    state.isDisplayed = false
  }
  transcriptClosedBus.emit()
  document.removeEventListener('keydown', globalEscape)
  untrapFocus()
}

const trapFocus = () => {
  lastActiveElement = document.activeElement
  document.addEventListener('keydown', handleKey)
}

const handleKey = (e) => {
  var KEYCODE_TAB = 9
  var isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB

  if (!isTabPressed) {
    return
  }

  if (e.shiftKey) {
    /* shift + tab */ if (document.activeElement === firstButton) {
      ;(lastButton as unknown as HTMLButtonElement).focus()
      e.preventDefault()
    }
  } /* tab */ else {
    if (document.activeElement === lastButton) {
      ;(firstButton as unknown as HTMLButtonElement).focus()
      e.preventDefault()
    }
  }
}

const untrapFocus = () => {
  document.removeEventListener('keydown', handleKey)
  lastActiveElement.focus()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["absolute left-0 top-0 w-full h-full", {
      'pointer-events-none': !state.isOpen,
      'pointer-events-auto': state.isOpen,
      invisible: !state.isDisplayed,
    }])
  }, [
    _createElementVNode("button", {
      id: "transcript-close-bg-btn",
      class: "absolute left-0 top-0 w-full h-full",
      onClick: closeViewer,
      tabindex: state.isOpen ? 520 : -1
    }, null, 8, _hoisted_1),
    _createElementVNode("div", {
      id: "transcriptWrapper",
      ref_key: "transcriptWrapper",
      ref: transcriptWrapper,
      class: "absolute w-full h-full left-0 top-0 flex items-center justify-center pointer-events-none scale-0"
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(interfaceText).audioTranscript), 1),
          _createElementVNode("button", {
            id: "transcript-close-btn",
            class: "sm:hidden",
            onClick: closeViewer,
            tabindex: state.isOpen ? 510 : -1
          }, [
            _createVNode(CloseIcon)
          ], 8, _hoisted_5),
          _createVNode(BasicButton, {
            text: _unref(interfaceText).close,
            variant: _unref(ButtonVariant).DarkOutline,
            sizing: _unref(ButtonSizing).Compact,
            class: "hidden sm:flex",
            onClick: closeViewer
          }, {
            icon: _withCtx(() => [
              _createVNode(CloseIcon)
            ]),
            _: 1
          }, 8, ["text", "variant", "sizing"])
        ]),
        _createElementVNode("h3", _hoisted_6, _toDisplayString(state.title), 1),
        _createElementVNode("div", {
          class: "transcript-body text-bodyXl sm:text-bodyLongRead grow overflow-scroll pb-10 pr-4 flex flex-col gap-5",
          style: _normalizeStyle({
            'mask-image':
              'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 90%, rgba(0,0,0,0) 100%)',
          }),
          innerHTML: state.text
        }, null, 12, _hoisted_7)
      ], 512)
    ], 512)
  ], 2))
}
}

})